import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { createClient } from '@/lib/supabase/client'

const formSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  document: z.string().min(11, 'Documento inválido'),
  email: z.string().email('Email inválido').optional().nullable(),
  phone: z.string().min(10, 'Telefone inválido').optional().nullable(),
})

export function CompanyForm() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const supabase = createClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      document: '',
      email: '',
      phone: '',
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      // Inserir empresa
      const { data: company, error: companyError } = await supabase
        .from('company')
        .insert({
          name: values.name,
          document: values.document,
          email: values.email,
          phone: values.phone,
        })
        .select()
        .single()

      if (companyError) throw companyError

      // Criar vínculo em user_company
      if (!user?.id) {
        toast.error('Usuário não autenticado. Faça login novamente.')
        return
      }
      const userId = user.id
      const { error: userCompanyError } = await supabase
        .from('user_company')
        .insert({
          user_id: userId,
          company_id: company.id,
          role: 'admin',
        })

      if (userCompanyError) throw userCompanyError

      // Atualizar current_company_id em users
      const { error: userError } = await supabase
        .from('users')
        .update({
          current_company_id: company.id,
        })
        .eq('id', userId)

      if (userError) throw userError

      toast.success('Empresa criada com sucesso!')
      navigate('/dashboard')
    } catch (error) {
      console.error(error)
      toast.error('Erro ao criar empresa. Tente novamente.')
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome da Empresa</FormLabel>
              <FormControl>
                <Input placeholder="Digite o nome da empresa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="document"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CNPJ/CPF</FormLabel>
              <FormControl>
                <Input placeholder="Digite o CNPJ ou CPF" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Digite o email da empresa" {...field} value={String(field.value ?? '')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Telefone</FormLabel>
              <FormControl>
                <Input placeholder="Digite o telefone da empresa" {...field} value={String(field.value ?? '')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          Criar Empresa
        </Button>
      </form>
    </Form>
  )
} 